import React, { useState, useEffect, useRef } from "react";
import Select, { components } from "react-select";
import { useSelector, useDispatch } from "react-redux";
import {
  white,
  textPrimary,
  textSecondary,
  newBorder,
  primary,
  backgroundLight,
  hoverItemColor,
} from "../../utils/colors";
import { useTranslation } from "react-i18next";
import { Flex, Box, Text, Spinner, Stack, Icon } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { getPatientTreatmentPlansAction } from "../../actions/patientActions";
import dayjs from "dayjs";
import { shade } from "polished";
import SearchInput from "../input/SearchInput";
import StatusBadge from "../badges/StatusBadge";
import { TbAlertCircle } from "react-icons/tb";

const TreatmentPlanSelectNew = ({
  patientId,
  onChange,
  selectedPlans = [],
  patientName,
}) => {
  // TRANSLATION
  const { t } = useTranslation();

  const [showSelectedPlans, setShowSelectedPlans] = useState(false); // Show selected plans by default

  // Function to remove a selected plan
  const handleRemovePlan = (planId) => {
    onChange(selectedPlans.filter((plan) => plan._id !== planId));
  };

  // REDUX
  const dispatch = useDispatch();

  const getPatientTreatmentPlans = useSelector(
    (state) => state.getPatientTreatmentPlans
  );
  const { treatmentPlans, loading, error, pages } = getPatientTreatmentPlans;

  // PAGINATION
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const pageSizes = [4, 10, 25, 100];

  //SORTING
  const [sortField, setSortField] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("asc");

  // SEARCH
  const [searchTerm, setSearchTerm] = useState("");
  const [value, setValue] = useState(null);

  // LOCAL SEARCH
  const [localSearchTerm, setLocalSearchTerm] = useState("");
  const [filteredPlans, setFilteredPlans] = useState([]);

  // HANDLERS
  const handleChange = (selectedOption) => {
    if (!selectedOption || !selectedOption._id) {
      console.error("❌ Invalid plan selected:", selectedOption);
      return;
    }

    // console.log("🛠 Clicked on:", selectedOption.treatmentPlanID);
    // console.log(
    //   "🔎 Currently selected plans:",
    //   selectedPlans.map((p) => p.treatmentPlanID)
    // );

    // Check if plan is already selected
    const isAlreadySelected = selectedPlans.some(
      (plan) => plan._id === selectedOption._id
    );

    let updatedPlans;

    if (isAlreadySelected) {
      // ✅ Remove plan from selection
      updatedPlans = selectedPlans.filter(
        (plan) => plan._id !== selectedOption._id
      );
      // console.log("🗑️ Removing:", selectedOption.treatmentPlanID);
    } else {
      // ✅ Add new plan to selection
      updatedPlans = [...selectedPlans, selectedOption];
      // console.log("➕ Adding:", selectedOption.treatmentPlanID);
    }

    // console.log(
    //   "✅ Updated selected plans:",
    //   updatedPlans.map((p) => p.treatmentPlanID)
    // );

    // 🔥 Pass the updated selection list back to the parent
    onChange(updatedPlans);
  };

  // Filter out selected plans
  const availablePlans = treatmentPlans?.filter(
    (plan) => !selectedPlans.some((selected) => selected._id === plan._id)
  );

  useEffect(() => {
    dispatch(
      getPatientTreatmentPlansAction(
        patientId,
        page, // Pass page number
        pageSize, // Pass page size
        sortField, // Pass sorting field
        sortOrder, // Pass sorting order
        searchTerm, // Pass search term
        true // Set 'all' to true to fetch all records
      )
    );
  }, [dispatch, patientId, page, pageSize, sortField, sortOrder, searchTerm]);

  const isCurrentYear = dayjs().format("YYYY") === dayjs().format("YYYY");

  const nameAlt = "N/A";
  const nameInitials = "N/A";

  // Custom Option component to display the image
  const CustomOption = (props) => {
    const isSelected = selectedPlans.some(
      (plan) => plan._id === props.data._id
    );

    const isCancelled = props.data.status === "cancelled"; // ❌ Check if canceled

    return (
      <components.Option {...props} isDisabled={isCancelled}>
        <Flex
          align="center"
          w="100%"
          // h="100%"
          p="1rem 1.5rem"
          // h="48px"
          role="group"
          // _hover={{
          //   bg: hoverItemColor,
          //   color: textPrimary,
          //   cursor: "pointer",
          // }}
          borderBottom={`1px solid ${newBorder}`}
          bg={isSelected ? primary : "transparent"} // ✅ Highlight selected
          color={isSelected ? white : textPrimary} // ✅ Adjust text color
          cursor={isCancelled ? "not-allowed" : "pointer"} // ❌ Disable pointer
          _hover={
            isCancelled
              ? {} // No hover effect if canceled
              : {
                  bg: isSelected ? shade(0.1, primary) : hoverItemColor,
                  color: isSelected ? white : textPrimary,
                }
          }
          onClick={() => !isCancelled && handleChange(props.data)} // Prevent click
          // isDisabled={props.data.disabled}
        >
          <Flex align="center" justify="space-between" w="100%">
            <Flex align="center">
              <Text
                color={
                  isCancelled ? textSecondary : isSelected ? white : textPrimary
                }
                _groupHover={{
                  color: isCancelled
                    ? textSecondary
                    : isSelected
                    ? white
                    : textPrimary,
                }}
                fontWeight="500"
                fontSize="sm"
                mr="1rem"
              >
                {props.data.treatmentPlanID}
              </Text>

              <Stack gap={0} overflow="hidden" mr="1.5rem">
                {props.data.createdAt && (
                  <Text
                    fontSize="sm"
                    color={
                      isCancelled
                        ? textSecondary
                        : isSelected
                        ? white
                        : textSecondary
                    }
                    fontWeight="500"
                    whiteSpace="nowrap"
                    overflow="hidden"
                    isTruncated
                    textTransform="capitalize"
                  >
                    {dayjs(props.data.createdAt).format("MMMM D")}
                    {!isCurrentYear && (
                      <Text as="span" isTruncated maxWidth="100px">
                        , {dayjs(props.data.createdAt).format("YYYY")}
                      </Text>
                    )}
                  </Text>
                )}
              </Stack>
            </Flex>

            <Flex
              align="center"
              justify="flex-end"
              bg="transparent"
              borderRadius="10rem"
              _groupHover={{
                bg: "transparent",
              }}
            >
              <Flex align="center">
                <Text
                  fontSize="sm"
                  color={isSelected ? white : textSecondary}
                  fontWeight="500"
                  textTransform="lowercase"
                  // mr="1rem"
                >
                  {props.data.services?.length || 0}{" "}
                  {t(
                    `service.${
                      props.data.services?.length === 1 ? "service" : "services"
                    }`
                  )}
                </Text>
                <Text
                  fontSize="xs"
                  color={isSelected ? white : textSecondary}
                  fontWeight="300"
                  mx="0.5rem"
                >
                  |
                </Text>
                <Text
                  fontSize="sm"
                  color={isSelected ? white : textSecondary}
                  fontWeight="500"
                  mr="1rem"
                >
                  {props.data.totalPrice} €
                </Text>
              </Flex>

              <StatusBadge
                isSmall
                options="treatment"
                status={props.data.status}
                ml={2}
                fixedWidth="110px"
              />
            </Flex>
          </Flex>
        </Flex>
      </components.Option>
    );
  };
  // LOCAL SEARCH
  useEffect(() => {
    const lowerKeyword = localSearchTerm.toLowerCase();

    // Filter plans based on localSearchTerm
    const searchedPlans = treatmentPlans.filter((plan) =>
      plan.treatmentPlanID.toLowerCase().includes(lowerKeyword)
    );

    // Separate selected & non-selected plans
    const preselectedPlans = selectedPlans.map((plan) => {
      return treatmentPlans.find((tp) => tp._id === plan._id) || plan;
    });

    const remainingPlans = searchedPlans.filter(
      (plan) => !selectedPlans.some((sp) => sp._id === plan._id)
    );

    // Sort non-selected plans by treatmentPlanID
    remainingPlans.sort((a, b) =>
      b.treatmentPlanID.localeCompare(a.treatmentPlanID, undefined, {
        numeric: true,
      })
    );

    // Merge pinned plans first
    setFilteredPlans([...preselectedPlans, ...remainingPlans]);
  }, [localSearchTerm, treatmentPlans, selectedPlans]);

  const handleLocalSearchChange = (e) => setLocalSearchTerm(e.target.value);

  // SORT PLANS
  const sortedPlans = [...treatmentPlans].sort((a, b) =>
    b.treatmentPlanID.localeCompare(a.treatmentPlanID, undefined, {
      numeric: true,
    })
  );

  // Separate preselected from newly selected
  const preselectedPlans = selectedPlans.map(
    (plan) => treatmentPlans.find((tp) => tp._id === plan._id) || plan
  );

  const newSelectedPlans = selectedPlans.filter(
    (plan) => !treatmentPlans.some((tp) => tp._id === plan._id)
  );

  // Merge: preselected first, then sorted remaining plans
  const finalPlans = [
    ...preselectedPlans,
    ...sortedPlans.filter(
      (plan) => !selectedPlans.some((sp) => sp._id === plan._id)
    ),
  ];

  // LOG
  // console.log("treatmentPlans", treatmentPlans);
  // console.log("selectedPlans", selectedPlans);
  // console.log("showSelectedPlans", showSelectedPlans);
  // console.log("availablePlans", availablePlans);
  // console.log("preselectedPlans", preselectedPlans);
  // console.log("newSelectedPlans", newSelectedPlans);
  // console.log("finalPlans", finalPlans);

  return (
    <>
      {loading ? (
        <Flex w="100%" align="center" justify="center" p="3rem 0">
          <Spinner size="lg" color={primary} />
        </Flex>
      ) : (
        <Box
          w="100%"
          h="100%"
          as={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: 0.2 }}
        >
          <Text
            fontSize="sm"
            fontWeight="500"
            color={textSecondary}
            m="0 0 1rem"
            textAlign="center"
          >
            {patientName} {t("treatmentPlan.patientTreatmentPlans")}
          </Text>
          <Flex
            w="100%"
            align="center"
            justify="space-between"
            // m="0 0 1rem"
            // gap="0.5rem"
          >
            <Flex align="center" w="100%">
              <SearchInput
                placeholder={t("treatmentPlan.searchSimple")}
                onChange={handleLocalSearchChange}
                searchName={localSearchTerm}
                p="0"
                m="0"
              />
            </Flex>
          </Flex>

          <Box w="100%">
            <Select
              menuPortalTarget={null}
              menuIsOpen
              autoFocus
              width="100%"
              onChange={handleChange}
              // isSearchable
              value={
                treatmentPlans.find((option) => option.value === value) || null
              }
              // onChange={onChange}
              options={filteredPlans.map((plan) => ({
                ...plan,
                isDisabled: plan.status === "cancelled", // 🚫 Disable canceled plans
              }))}
              placeholder={t("treatmentPlan.searchTreatmentPlan")}
              noOptionsMessage={() => (
                <Flex p="3rem 1rem" align="center" justify="center">
                  <Text fontSize="sm" fontWeight="500" color={textSecondary}>
                    {t("noItems.noPatientTreatmentPlans")}
                  </Text>
                </Flex>
              )} // Custom message
              components={{
                Option: (props) => (
                  <CustomOption {...props} selectedPlans={selectedPlans} />
                ),
              }} // ✅ Pass selected plans
              styles={{
                menu: (baseStyles) => ({
                  ...baseStyles,
                  borderRadius: "1rem",
                  overflow: "hidden",
                  border: `1px solid ${newBorder}`,
                  position: "relative",
                  //  boxShadow: `0 0 0 1px ${newBorder}`,
                  boxShadow: "none",
                  zIndex: "0",
                  padding: "0",
                  margin: "1rem 0 0",
                  color: textPrimary,
                }),

                menuList: (baseStyles) => ({
                  ...baseStyles,
                  padding: "0rem 0rem 0",
                  maxHeight: "269px", // Set max height for the dropdown
                  overflowY: "auto", // Enable vertical scrolling
                }),
                group: (baseStyles) => ({
                  ...baseStyles,
                  padding: "0",
                  margin: "0",
                }),
                groupHeading: (baseStyles) => ({
                  ...baseStyles,
                  fontSize: "11px",
                  fontWeight: "500",
                  textTransform: "uppercase",
                  letterSpacing: "0.369px",
                  backgroundColor: backgroundLight,
                  borderBottom: `1px solid ${newBorder}`,
                  padding: "0.75rem 1.5rem",
                  margin: "0",
                  color: textSecondary,
                }),
                option: (baseStyles, state) => ({
                  ...baseStyles,
                  fontSize: "0.875rem",

                  padding: "0px 0px",
                  borderBottom: `0px solid ${newBorder}`,
                  margin: "0",
                  backgroundColor: state.hover ? "white" : "white",
                }),

                control: (baseStyles, state) => ({
                  ...baseStyles,
                  borderColor: state.isFocused ? newBorder : newBorder,
                  color: textSecondary,
                  borderRadius: "10rem",
                  backgroundColor: `${white}`,
                  border: `1px solid ${newBorder}`,
                  fontSize: "0.938rem",
                  fontWeight: "500",
                  height: "3rem",
                  width: "100%",
                  minWidth: "500px",
                  padding: "0 0.25rem",
                  display: "none",
                  ":focus-visible": {
                    borderColor: primary,
                    boxShadow: `0 0 0 1px ${newBorder}`,
                  },
                }),
                container: (baseStyles) => ({
                  ...baseStyles,
                  width: "100%",
                }),

                singleValue: (baseStyles, provided, state) => {
                  const opacity = state.isDisabled ? 0.5 : 1;
                  const transition = "opacity 300ms";

                  return {
                    ...provided,
                    opacity,
                    transition,
                    color:
                      state.data.value === "" ? "desired color" : textPrimary,
                  };
                },
              }}
            />
          </Box>
          <Flex align="center" justify="center" m="1rem 0 0">
            <Text
              fontSize="xs"
              fontWeight="500"
              m="0 auto 0.5rem"
              maxW="90%"
              color={textSecondary}
            >
              <Icon
                as={TbAlertCircle}
                color={primary}
                fontSize="0.875rem"
                position="relative"
                top="3px"
                mr="0.125rem"
              />
              {t("treatmentPlan.selectTreatmentPlanWarning")}
            </Text>
          </Flex>
        </Box>
      )}
    </>
  );
};

export default TreatmentPlanSelectNew;
